import { styled } from 'styled-components';

import { color, dimension } from '@creditornot/cb-ingredients/design-tokens';
import { Empty } from '@creditornot/cb-placeholders';
import { Spinner } from '@creditornot/cb-progress';
import { ApiErrorNotification } from 'common/components/cookbook/ApiErrorNotification';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { DataTable, DataTableProps } from 'common/components/cookbook/DataTable/DataTable';
import { SelectionState } from 'common/components/cookbook/DataTable/types';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useUserLocale } from 'common/hooks/useUserLocale';
import { media } from 'common/media';
import { AxiosDelistingRecommendationsError } from 'common/types';
import { formatMoney } from 'common/utils/moneyUtils';
import { formatNumber } from 'common/utils/numberUtils';
import { ProductCard } from 'features/selection-recommendations/common/ProductCard';
import { RecommendationReasons } from 'features/selection-recommendations/common/RecommendationReasons/RecommendationReasons';
import { Vendors } from 'features/selection-recommendations/common/Vendors/Vendors';
import { getListingDuration, getPriceRange } from 'features/selection-recommendations/utils';

import { DelistingRecommendationSubList } from './DelistingRecommendationSubList';
import { DelistingRecommendationItem } from './types';

const EmptyStateContainer = styled(ContentContainer)`
  padding: ${dimension.spaceBroadMedium};
`;

const SpinnerContainer = styled(ContentContainer)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

const StyledDataTable = styled(DataTable as React.FC<DataTableProps<DelistingRecommendationItem>>)`
  @media ${media.lg} {
    [role='cell']:nth-child(2),
    [role='columnheader']:nth-child(2) {
      border-right: 1px solid ${color.border};
      left: 48px;
      position: sticky;
      z-index: 0;
    }
  }
`;

type Props = {
  error: AxiosDelistingRecommendationsError | null;
  groupedByProduct: DelistingRecommendationItem[];
  isFetching: boolean;
  isLoading: boolean;
  rowSelection: {
    onRowSelectChange: (value: 'ALL' | SelectionState) => void;
    selectedRows: string[] | 'ALL';
  };
  hasNextPage: boolean;
  fetchNextPage: () => void;
  totalCount: number | undefined;
};

export const DelistingRecommendationsList = ({
  error,
  groupedByProduct,
  isFetching,
  isLoading,
  rowSelection,
  hasNextPage,
  fetchNextPage,
  totalCount,
}: Props) => {
  const f = useFormatMessage();
  const locale = useUserLocale();

  if (error) {
    return (
      <ContentContainer>
        <ApiErrorNotification error={error} />
      </ContentContainer>
    );
  }

  if (!groupedByProduct.length && !isLoading) {
    return (
      <EmptyStateContainer>
        <Empty
          title={f('selection_recommendations.no_recommendations')}
          description={f('selection_recommendations.no_recommendations_description')}
        />
      </EmptyStateContainer>
    );
  }

  if (isLoading) {
    return (
      <SpinnerContainer>
        <Spinner size="large" />
      </SpinnerContainer>
    );
  }

  return (
    <StyledDataTable
      dataTestId="delisting-recommendations-list"
      isStickyHeader={true}
      stickyHeaderOffset={65}
      hideScrollbars={true}
      fetchOnScroll={{
        hasNextPage: hasNextPage || false,
        onFetchMore: fetchNextPage,
        isFetching,
      }}
      dataSource={groupedByProduct ?? []}
      renderSubComponent={(record) => {
        return <DelistingRecommendationSubList parent={record} />;
      }}
      rowSelection={rowSelection}
      columns={[
        {
          id: 'product',
          alignNameVertical: 'middle',
          header: f('selection_recommendations.recommendations_count', {
            count: formatNumber(locale, totalCount ?? 0),
          }),
          width: 400,
          render: ({ record: { product } }) => {
            return (
              <ProductCard
                productId={product.id}
                imgSrc={product.image?.url}
                productName={product.name}
                productGtin={product.gtin ?? undefined}
                productCode={product.code}
                currentTab="DELISTING_RECOMMENDATIONS"
              />
            );
          },
        },
        {
          id: 'expectedBusinessImpact',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          info: f(
            'selection_recommendations.delisting_recommendation_expected_business_impact_column_explanation',
          ),
          header: f('selection_recommendations.expected_business_impact'),
          width: 150,
          render: ({ record: { expectedBusinessImpact } }) => {
            return expectedBusinessImpact?.pureProfitMarginImpact?.amount !== undefined
              ? formatMoney(locale, expectedBusinessImpact.pureProfitMarginImpact)
              : '-';
          },
        },
        {
          id: 'reasons',
          alignNameVertical: 'middle',
          header: f('selection_recommendations.reason'),
          width: 'auto',
          render: ({ record: { reasons } }) => {
            return <RecommendationReasons reasons={reasons} />;
          },
        },
        {
          id: 'salesPrice',
          header: f('selection_recommendations.sales_price'),
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 150,
          render: ({ record: { salesPriceRange } }) => getPriceRange(salesPriceRange, locale),
        },
        {
          id: 'soldUnits',
          header: (
            <>
              <div>{f('selection_recommendations.sold_units')}</div>
              <div>{f('selection_recommendations.sold_units_explanation')}</div>
            </>
          ),
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 150,
          render: ({ record: { soldUnitsPast90Days } }) => {
            return soldUnitsPast90Days ? formatNumber(locale, soldUnitsPast90Days) : '-';
          },
        },
        {
          id: 'costPrice',
          header: f('selection_recommendations.cost_price'),
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 150,
          render: ({ record: { costPriceRange } }) => {
            if (!costPriceRange) return f('common.not_available_acronym');
            return getPriceRange(costPriceRange, locale);
          },
        },
        {
          id: 'ppm',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          header: f('selection_recommendations.ppm_percentage'),
          width: 100,
          render: ({ record: { pureProfitMargin } }) => {
            if (!pureProfitMargin) return '-';
            return `${formatNumber(locale, pureProfitMargin)}%`;
          },
        },
        {
          id: 'ppmGap',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          info: f('selection_recommendations.recommendation_ppm_gap_column_explanation'),
          header: (
            <>
              <div>{f('selection_recommendations.ppm_delta')}</div>
              <div>{f('selection_recommendations.ppm_delta_explanation')}</div>
            </>
          ),
          width: 150,
          render: ({ record: { pureProfitMarginGapBps } }) => {
            const ppmGap = formatNumber(locale, pureProfitMarginGapBps);

            return pureProfitMarginGapBps && pureProfitMarginGapBps > 0
              ? `+${ppmGap}`
              : ppmGap || '-';
          },
        },
        {
          id: 'priceIndex',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          header: f('selection_recommendations.price_index'),
          info: f('selection_recommendations.recommendation_price_index_column_explanation'),
          width: 120,
          render: ({ record: { priceIndex } }) => {
            if (!priceIndex) return '-';
            return formatNumber(locale, Number(priceIndex), 2);
          },
        },
        {
          id: 'stockCount',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          header: f('selection_recommendations.stock_count'),
          width: 120,
          render: ({ record: { stockCount } }) => {
            if (!stockCount) return '0';
            return formatNumber(locale, stockCount);
          },
        },
        {
          id: 'shrink',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          header: f('selection_recommendations.shrink'),
          width: 120,
          render: ({ record: { shrinkageAbsolute } }) => {
            return shrinkageAbsolute ? formatMoney(locale, shrinkageAbsolute) : '-';
          },
        },
        {
          id: 'shrinkage',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          header: f('selection_recommendations.shrinkage_percentage'),
          width: 120,
          render: ({ record: { shrinkagePercentage, shrinkagePercentageIsInfinite } }) => {
            if (shrinkagePercentageIsInfinite) return '∞';
            return shrinkagePercentage ? `${formatNumber(locale, shrinkagePercentage, 2)}%` : '-';
          },
        },
        {
          id: 'vendor',
          alignNameVertical: 'middle',
          header: f('selection_recommendations.vendor'),
          width: 200,
          info: f('selection_recommendations.recommendation_vendor_column_explanation'),
          render: ({ record: { vendors } }) => {
            return vendors ? <Vendors items={vendors} /> : '-';
          },
        },
        {
          id: 'listedFor',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          header: f('selection_recommendations.listed_for'),
          width: 120,
          render: ({ record: { listedSince } }) => {
            const listingDuration = getListingDuration(listedSince, locale);
            return f('selection_recommendations.listed_for_weeks', {
              count: listingDuration,
            });
          },
        },
        {
          id: 'basketPenetration',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          header: f('selection_recommendations.basket_penetration'),
          width: 160,
          render: ({ record: { basketPenetrationPercentage } }) => {
            if (!basketPenetrationPercentage) return '-';
            return `${formatNumber(locale, basketPenetrationPercentage, 2)}%`;
          },
        },
        {
          id: 'basketPenetrationPHL2Median',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          header: f('selection_recommendations.basket_penetration_phl2_median'),
          width: 160,
          render: ({ record: { basketPenetrationPercentagePHL2Median } }) => {
            if (!basketPenetrationPercentagePHL2Median) return '-';
            return `${formatNumber(locale, basketPenetrationPercentagePHL2Median, 2)}%`;
          },
        },
        {
          id: 'profitPerUnit',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          header: f('selection_recommendations.profit_per_unit'),
          width: 140,
          render: ({ record: { profitPerUnit } }) =>
            profitPerUnit ? formatMoney(locale, profitPerUnit) : '-',
        },
        {
          id: 'profitPerUnitPHL2Median',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          header: f('selection_recommendations.profit_per_unit_phl2_median'),
          width: 140,
          render: ({ record: { profitPerUnitPHL2Median } }) =>
            profitPerUnitPHL2Median ? formatMoney(locale, profitPerUnitPHL2Median) : '-',
        },
        {
          id: 'revenue',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          header: f('selection_recommendations.revenue'),
          width: 150,
          render: ({ record: { totalRevenue } }) => formatMoney(locale, totalRevenue),
        },
      ]}
    />
  );
};
